<!--
  - Copyright (c) 2021. Trade Research Advisory (Pty) Ltd, P.O.Box 336, Cornwall Hill, Irene, 0178
  - #  This file forms part of the Trade-DSM application frontend.
  - #  https://github.com/Tooblippe/trade-dsm-frontend
  -->

<template>
  <v-data-table
    :headers="gen_cube()['headers']"
    :items="gen_cube()['items']"
    :items-per-page="5"
    :sort-by.sync="sort_by"
    :sort-desc="sort_desc"
    class="elevation-1 ma-3 mytable"
    dense
    hide-default-header
    :id="table_id"
    :footer-props="{
      'items-per-page-options': calc_items_per_page(gen_cube()['items'].length),
    }"
  >
    <!-- custom header-->
    <template v-slot:header="{props}">
      <th
        v-for="(head, i) in props.headers"
        :key="head.id"
        :class="{
          table_header_color: i > 0,
          table_header_color_ix: i === 0,
          section: head.text === 'Section',
          opportunities: head.text === 'Opportunities',
          tariff: head.text === 'Tariff*<br>[%]',
          reo: head.text === 'REO<sub>x,y</sub>',
        }"
      >
        <v-btn
          v-if="i === 0"
          x-small
          text
          class="ml-3"
          @click="
            html_to_xls_download(
              table_id,

              $store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.markets
                .C_Code.length === 1
                ? '_' +
                    codeToMarket(
                      $store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections
                        .markets.C_Code[0],
                    )
                : '',
            )
          "
          ><v-icon class="green--text mb-12 mr-1">mdi-microsoft-excel</v-icon></v-btn
        >

        <v-tooltip top v-if="(i > 1 && head.text !== 'Description') || i > 2">
          <template v-slot:activator="{on, attrs}">
            <span
              v-html="head.text"
              @click="sort_clicked(head.value)"
              v-on="on"
              :class="{'ml-4': i === 1}"
            ></span>
            <span>
              <v-icon v-if="sort_by === head.value" class="text--accent-4">
                {{ sort_desc ? 'mdi-arrow-down' : 'mdi-arrow-up' }}
              </v-icon>
            </span>
          </template>
          <span
            >Sort {{ sort_desc && sort_by === head.value ? 'ascending' : 'descending' }}
          </span>
        </v-tooltip>

        <template v-else>
          <template v-if="head.text === 'Code (HS6)'">
            <v-checkbox small v-model="enable_market_click" class="ml-4">
              <template v-slot:label>
                <span class="caption">{{
                  !enable_market_click
                    ? 'Enable Selection Shortcuts'
                    : 'Disable Selection Shortcuts'
                }}</span>
              </template>
            </v-checkbox>
          </template>

          <v-tooltip top>
            <template v-slot:activator="{on, attrs}">
              <span v-on="on" :class="{hs6_code: i === 1, hs6_disription: i === 2}">
                <span @click="sort_clicked(head.value)" v-html="head.text"></span>
                <span>
                  <v-icon v-if="sort_by === head.value && i !== 0" class="text--accent-4">
                    {{ sort_desc ? 'mdi-arrow-down' : 'mdi-arrow-up' }} </v-icon
                  >--
                </span>
              </span>
            </template>
            <span
              >Sort {{ sort_desc && sort_by === head.value ? 'ascending' : 'decsending' }}
            </span>
          </v-tooltip>
        </template>
      </th>
    </template>

    //Number Column
    <template v-slot:item.id_col="{item}">
      <div class="text-right pr-1 number-col">{{ item['id_col'] }}</div>
    </template>

    //Code only Column
    <template v-slot:item.HS6_Code="{item}">
      <span
        v-if="enable_market_click"
        class="text-left reo-table-selector"
        @click="table_click(item['HS6_Code'])"
      >
        {{ item['HS6_Code'] }}
      </span>
      <span v-else class="text-left reo-table-selector-off">
        {{ item['HS6_Code'] }}
      </span>
    </template>

    //discription Column
    <template v-slot:item.discription="{item}">
      <div class="text-start">
        {{ item['discription'] }}
      </div>
    </template>

    // REO Column
    <template v-slot:item.REO="{item}">
      <template v-if="item['REO_col'] >= 1 && item['REO_row'] >= 1">
        <v-tooltip bottom class="green">
          <template v-slot:activator="{on, attrs}">
            <p v-html="item['REO']" class="reo-col reo" v-on="on"></p>
          </template>
          <span
            >REO<sub>{{ item['REO_col'] }},{{ item['REO_row'] }}</sub>
            <br />
            {{ supply_text(item['REO_col'], item['HS6_Code'])['tooltip_text'] }}
            <br />
            {{ demand_text(item['REO_row'], item['HS6_Code'])['tooltip_text'] }}
          </span>
        </v-tooltip>
      </template>
      <span v-else class="reo-col reo ml-8">-</span>
    </template>

    // Tariff
    <template v-slot:item.ad_valorem_tariff="{item}">
      <v-tooltip
        left
        v-if="Math.round(parseFloat(item.ad_valorem_tariff) * 100 * 100) / 100 > 0"
      >
        <template v-slot:activator="{on, attrs}">
          <div class="text-center" v-on="on">
            <a :href="itc_url(item)" target="_blank" class="no-link">
              {{ ((parseFloat(item.ad_valorem_tariff) * 100 * 100) / 100).toFixed(2) }}</a
            >
          </div>
        </template>
        <span>Click to view ITC tariff information</span>
      </v-tooltip>
      <span v-else class="ml-2">-</span>
    </template>

    //Rank
    <template v-if="table_id === 'reo_hs6_code'" v-slot:item.num_col="{item}">
      <div
        class="opportunities red-"
        v-bind:class="{'red--text': item['Market_Rank'] === 0}"
      >
        {{ item['Market_Rank'] }}/{{ item['Market_REO_Count'] }}
      </div>
    </template>
    <template v-else v-slot:item.opportunities="{item}">
      <div class="opportunities">
        {{ item['opportunities'] }}
      </div>
    </template>

    //Prov_Share
    <template v-slot:item.share="{item}">
      <div class="text-center">
        {{ item['share'] }}
      </div>
    </template>

    //A
    <template v-slot:item.Avg6Major="{item}">
      <div class="text-center">
        {{ vFmt(item['Avg6Major'] * value_convertion, 2) }}
      </div>
    </template>

    //A1
    <template v-slot:item.Halal_Adj_Potential="{item}">
      <div class="text-center">
        {{ vFmt(item['Halal_Adj_Potential'] * value_convertion, 2) }}
      </div>
    </template>

    //B
    <template v-slot:item.HM_Exp_TM="{item}">
      <div class="text-center">
        {{
          parseFloat(vFmt(item['HM_Exp_TM'] * value_convertion, 2)) > 0
            ? vFmt(item['HM_Exp_TM'] * value_convertion, 2)
            : '-'
        }}
      </div>
    </template>

    //C
    <template v-slot:item.HM_TM_MShr="{item}">
      <div class="text-center">
        {{
          Math.round(parseFloat(item['HM_TM_MShr']) * 100 * 10) / 10 > 0
            ? Math.round(parseFloat(item['HM_TM_MShr']) * 100 * 10) / 10
            : '-'
        }}
      </div>
    </template>

    //D
    <template v-slot:item.row_d="{item}">
      <div class="text-center">
        {{
          Math.round((item['HM_Exp_TM'] / item['Avg6Major']) * 10) / 10 > 0
            ? Math.round((item['HM_Exp_TM'] / item['Avg6Major']) * 10) / 10
            : '-'
        }}
      </div>
    </template>

    //E
    <template v-slot:item.Size6Major="{item}">
      <div class="text-center">
        {{
          parseFloat(vFmt(item['Size6Major'] * value_convertion, 2)) > 0
            ? vFmt(item['Size6Major'] * value_convertion, 2)
            : '-'
        }}
      </div>
    </template>

    //F
    <template v-slot:item.row_f="{item}">
      <div class="text-center">
        {{
          vFmt(
            (item['TW_import_product'] - item['Size6Major'] - item['HM_Exp_TM']) *
              value_convertion,
            2,
          ) >= 0
            ? vFmt(
                (item['TW_import_product'] - item['Size6Major'] - item['HM_Exp_TM']) *
                  value_convertion,
                2,
              )
            : '-'
        }}
      </div>
    </template>

    //G
    <template v-slot:item.TW_import_product="{item}">
      <div class="text-center">
        {{ vFmt(item['TW_import_product'] * value_convertion, 2) }}
      </div>
    </template>

    //RCA
    <template v-slot:item.HM_TW_RCA="{item}">
      <div class="text-center">
        a
      </div>
    </template>
  </v-data-table>
</template>
<script>
const {
  html_to_xls_download,
} = require('@/components/application-level/js_functions/jsFunctions');

const {
  calc_items_per_page,
} = require('@/components/trade-dsm-application/mixins/calc_items_per_page');

export default {
  name: 'reo_table_products',
  props: {
    cube: String,
    table_id: String,
  },
  data() {
    return {
      enable_market_click: false,
      sort_by: 'Avg6Major',
      sort_desc: true,
    };
  },
  methods: {
    html_to_xls_download: html_to_xls_download,
    sort_clicked(column_name) {
      if (this.sort_by === column_name) {
        this.sort_desc = !this.sort_desc;
      } else {
        this.sort_desc = true;
        this.sort_by = column_name;
      }
    },

    itc_url(item) {
      let home_market_code = '';
      if ('Home_Market' in this.$store.state.tradeDSM.dsm_filter_setttings) {
        home_market_code = this.$store.state.tradeDSM.dsm_filter_setttings.Home_Market.slice(
          1,
        );
      }

      let target_market_code = '';
      if ('C_Code_x' in item) {
        target_market_code = item['C_Code_x'].slice(1);
      }
      target_market_code = this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.markets.C_Code[0].slice(
        1,
      );

      let hs6_code = '';
      if (
        this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.products
          .HS6_Code.length > 0
      ) {
        this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.products.HS6_Code[0].slice(
          2,
        );
      }
      let year = 'none';
      try {
        year = item['ad_valorem_tariff_year'];
      } catch (e) {
        // TODO - Move this to settings, it is still hard coded
        year = 2020;
      }

      return `https://macmap.org/en/query/customs-duties?reporter=${
        target_market_code === '158' ? '490' : target_market_code
      }&year=${year}&partner=${home_market_code}&product=${hs6_code}&level=6`;
      // https://macmap.org/en/query/customs-duties?reporter=792&year=2019&partner=036&product=010229&level=6
      // https://macmap.org/en/query/customs-duties?reporter=C792&year=2019&partner=036&product=010229&level=6
    },

    calc_items_per_page: calc_items_per_page,

    async table_click(code) {
      // const country_code = this.marketToCode(code);
      this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.products.HS6_Code.splice(
        0,
      );
      this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.products.HS4_Code.splice(
        0,
      );
      this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.products.HS2_Code.splice(
        0,
      );
      this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.products.HS2_Groups.splice(
        0,
      );

      await this.$set(
        this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.products
          .HS6_Code,
        0,
        code,
      );

      await this.$store.commit('set_product_search_term', code);

      await this.update_results_cube();
    },
    column_sum(cube, column) {
      // Get the correct results cube -'cube'
      // Sum the column - 'cpolumn' in the 'cube'
      let cube_values = this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META
        .query_results[cube];

      let acc = 0;
      for (let i = 0; i < cube_values.length; i++) {
        let item = cube_values[i];
        acc += item[column];
      }
      return acc;
    },

    build_reo_column(col, row) {
      if (col >= 1 && row >= 1) {
        return `REO<sub>${col},${row}</sub>`;
      } else {
        return '-';
      }
    },
    gen_cube() {
      if (!this.$store.state.app.disable_getters) {
        let cube_values = this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META
          .query_results[this.cube];

        let items = [];
        for (let i = 0; i < cube_values.length; i++) {
          let item = cube_values[i];

          // item['HM_TW_RCA'] = 1;
          // item['HM_TW_RTA'] = 2;

          item['num_col'] = i + 1;
          item['id_col'] = i + 1;
          item['name'] = item;

          // console.log(this.$store.state.app.selectedModelName);

          if (this.$store.state.app.selectedModelName !== 'National') {
            item['share'] =
              Math.round(
                this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.query_results
                  .shares_dict[item['HS6_Code']] * 10000,
              ) / 100;
          }

          if ('HS6_Code' in item) {
            item['discription'] = this.hs6_codeToDescription(item['HS6_Code']);
            item['SIC27'] = this.hs6_SIC_code(item['HS6_Code']);
            item['SIC27_Description'] = this.hs6_SIC_code_description(item['HS6_Code']);
          }
          if ('HS4_Code' in item) {
            item['discription'] = this.hs4_codeToDescription(item['HS4_Code']);
          }
          if ('HS2_Code' in item) {
            item['discription'] = this.hs2_codeToDescription(item['HS2_Code']);
          }

          item['REO'] = this.build_reo_column(item['REO_col'], item['REO_row']);
          item['row_d'] =
            Math.round((item['HM_Exp_TM'] / item['Avg6Major']) * 10) / 10 > 0
              ? Math.round((item['HM_Exp_TM'] / item['Avg6Major']) * 10) / 10
              : '-';
          item['row_f'] =
            this.vFmt(
              (item['TW_import_product'] - item['Size6Major'] - item['HM_Exp_TM']) *
                this.value_convertion,
              2,
            ) >= 0
              ? this.vFmt(
                  (item['TW_import_product'] - item['Size6Major'] - item['HM_Exp_TM']) *
                    this.value_convertion,
                  2,
                )
              : '-';

          // item['REO'] = this.build_reo_column(item['REO_col'], item['REO_row']);
          items.push(item);
        }
        // Main-Region
        // C_Region
        //C_Region

        let name_col = '';
        let col_name = '';

        if (this.cube === 'cube_HS6_Code') {
          name_col = 'HS6_Code';
          col_name = 'Code (HS6)';
        }
        if (this.cube === 'cube_HS4_Code') {
          name_col = 'HS4_Code';
          col_name = 'Heading (HS4)';
        }
        if (this.cube === 'cube_HS2_Code') {
          name_col = 'HS2_Code';
          col_name = 'Chapter (HS2)';
        }
        if (this.cube === 'cube_Section') {
          name_col = 'Major_Product_Groups';
          col_name = 'Section';
        }

        let return_values = {
          headers: [
            {
              text: '#',
              value: 'id_col',
              class: 'table_header_color',
            },
            {
              text: col_name,
              value: name_col,
              class: 'table_header_color',
            },

            ...(this.cube !== 'cube_Section'
              ? [
                  {
                    // conditionally add discription column
                    text: 'Description',
                    value: 'discription',
                    class: 'table_header_color',
                  },
                ]
              : []),

            ...(this.cube === 'cube_HS6_Code' &&
            this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META
              .report_type_to_generate !== 'MPMC'
              ? [
                  //conditional
                  {
                    text: 'REO<sub>x,y</sub>',
                    value: 'REO',
                    class: 'table_header_color',
                  },
                  //Tariff
                  {
                    text: 'Tariff*<br>[%]',
                    value: 'ad_valorem_tariff',
                    class: 'table_header_color',
                  },
                ]
              : []),

            //opportunities
            {
              text: this.table_id === 'reo_hs6_code' ? 'Rank' : 'Opportunities',
              value: this.table_id === 'reo_hs6_code' ? 'num_col' : 'opportunities',
              class: 'table_header_color',
            },

            // Number of potential markets

            ...(this.$store.state.app.selectedModelName !== 'National' &&
            this.table_id === 'reo_hs6_code'
              ? [
                  {
                    text:
                      this.$store.state.app.selectedModelName +
                      '<br>Provincial <br>  Share <br> %',
                    value: 'share',
                    class: 'table_header_color',
                  },
                ]
              : []),
            {
              text:
                '[A] <br> Total1 Realistic Export Potential to ' +
                'Target Market(s) <br>' +
                this.totalValueStringHTML(
                  this.$store.state.tradeDSM.dsm_filter_setttings.Product_Combinations
                    .results.trade_value,
                ),
              value: 'Avg6Major',
              class: 'table_header_color',
            },

            // A1
            {
              text:
                '[A1] <br> Halal reduced potential Target Market(s) <br>' +
                this.totalValueStringHTML(
                  this.column_sum(this.cube, 'Halal_Adj_Potential'),
                ),
              value: 'Halal_Adj_Potential',
              class: 'table_header_color',
            },

            //B
            {
              text:
                '[B]<br> Total Exports from Home Market to Target Market(s) <br>' +
                this.totalValueStringHTML(this.column_sum(this.cube, 'HM_Exp_TM')),
              value: 'HM_Exp_TM',
              class: 'table_header_color',
            },
            //C
            {
              text:
                '[C] = [B] / [G] <br>Total Exports from Home Market / Target Market(s) Total Imports<br>' +
                Math.round(
                  (this.column_sum(this.cube, 'HM_Exp_TM') /
                    this.column_sum(this.cube, 'TW_import_product')) *
                    100 *
                    10,
                ) /
                  10 +
                '%',
              value: 'HM_TM_MShr',
              class: 'table_header_color',
            },

            //D
            {
              text:
                '[D] = [B] / [A] <br>Total Exports from Home Market / Realistic Export Potential <br>' +
                Math.round(
                  (this.column_sum(this.cube, 'HM_Exp_TM') /
                    this.column_sum(this.cube, 'Avg6Major')) *
                    10,
                ) /
                  10 +
                '%',
              value: 'row_d',
              class: 'table_header_color',
            },

            //E
            {
              text:
                '[E]<br> Target Market(s) Imports from T6 (Excl. Home Market)<br>' +
                this.totalValueStringHTML(this.column_sum(this.cube, 'Size6Major')),
              value: 'Size6Major',
              class: 'table_header_color',
            },

            //F
            {
              text:
                '[F]<br> Target Market(s) Imports from Rest (Excl. Home Market)<br>' +
                this.totalValueStringHTML(
                  this.column_sum(this.cube, 'TW_import_product') -
                    this.column_sum(this.cube, 'Size6Major') -
                    this.column_sum(this.cube, 'HM_Exp_TM'),
                ),
              value: 'row_f',
              class: 'table_header_color',
            },

            //G
            {
              text:
                '[G]<br> Target Market(s) Total Imports<br>' +
                this.totalValueStringHTML(
                  this.column_sum(this.cube, 'TW_import_product'),
                ),
              value: 'TW_import_product',
              class: 'table_header_color',
            },

            //RCA
            {
              text: 'RCA_subscript_TW',
              value: 'HM_TW_RCA',
              class: 'table_header_color',
            },
            //RTA
            {
              text: 'RTA_subscript_TW',
              value: 'HM_TW_RTA',
              class: 'table_header_color',
            },
            //--------------------------------
          ],
          items: items,
        };

        if (this.cube === 'cube_HS6_Code') {
          this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.query_results[
            'MP1C_HS6_Cube'
          ] = return_values;
        }

        return return_values;
      } else
        return {
          headers: [],
          items: [],
        };
    },

    supply_text(REO_col, hs6_code) {
      let home_market = this.$store.state.app.selectedHomeCountry;
      // let target_market = this.codeToMarket(
      //     this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.markets
      //         .C_Code[0],
      // );
      let target_market = this.codeToMarket(
        this.$store.state.tradeDSM.dsm_filter_setttings.Filters.META.selections.markets
          .C_Code[0],
      );
      let tooltip_text = target_market + ' does not seem to have sufficient information';
      if (REO_col > 0) {
        switch (REO_col) {
          case 1:
            tooltip_text =
              home_market +
              ' to date has supplied none or relatively little of ' +
              target_market +
              '’s total import demand for ' +
              hs6_code;
            break;
          case 2:
            tooltip_text =
              home_market +
              ' to date has supplied an intermediately small share of ' +
              target_market +
              '’s total import demand for ' +
              hs6_code;
            break;
          case 3:
            tooltip_text =
              home_market +
              ' to date has supplied an intermediately large share of ' +
              target_market +
              '’s total import demand for ' +
              hs6_code;
            break;
          case 4:
            tooltip_text =
              home_market +
              ' to date has supplied a relatively large share of ' +
              target_market +
              '’s total import demand for ' +
              hs6_code;
            break;
        }
        return {
          tooltip_text,
        };
      } else {
        tooltip_text = '';
        return {tooltip_text};
      }
    },
    // Mve to backend
    demand_text(REO_row, target_market) {
      let tooltip_text =
        'This opportunity is not classified as a realistic opportunity and therefore the market characteristics are not determined.';

      if (REO_row > 0) {
        switch (REO_row) {
          case 1:
            tooltip_text =
              target_market +
              "'s import demand is relatively large in global terms, but stable or ‘flat’ from a growth perspective.";
            break;
          case 2:
            tooltip_text =
              target_market +
              "'s import demand shows that it is growing in both the short and long term, but relative to other global markets it is a small market.";
            break;
          case 3:
            tooltip_text =
              target_market +
              "'s import demand is relatively large in global terms and has exhibited strong growth in the last 2 years (for which data is available).";
            break;
          case 4:
            tooltip_text =
              target_market +
              "'s import demand is relatively large in global terms and has exhibited strong growth over the last 5 years (for which data is available), but less so in the latest 2 years.";
            break;
          case 5:
            tooltip_text =
              target_market +
              "'s import demand is relatively large in global terms and has exhibited strong growth over the latest 2 years as well as in the last 5 years (for which data is available).";
            break;
        }
        return {
          tooltip_text,
        };
      } else {
        tooltip_text = '';
        return {tooltip_text};
      }
    },
  },
};
</script>

<style scoped>
.country-selector {
  transition-duration: 0.2s;
  border-radius: 5px;
  padding: 2px 2px 2px 5px;
  background-color: yellow; /* Green */
  color: #1f3c5f;
  display: inline-block;
  width: 150px !important;
}

.country-selector:hover {
  color: yellow;
  cursor: pointer;
  padding: 2px 2px 2px 5px;
  background-color: #1f3c5f; /* Green */
}

.reo-table-selector {
  transition-duration: 0.2s;
  border-radius: 5px;
  padding: 2px 2px 2px 5px;
  color: #1f3c5f;
  display: inline-block;
  width: 80px !important;
}

.reo-table-selector:hover {
  color: yellow;
  cursor: pointer;
  padding: 2px 2px 2px 5px;
  background-color: #1f3c5f;
}

.reo-table-selector-off {
  transition-duration: 0.2s;
  border-radius: 5px;
  padding: 2px 2px 2px 5px;
  display: inline-block;
  width: 80px !important;
}

.reo-table-selector-off:hover {
  padding: 2px 2px 2px 5px;
}

.hs6_disription {
  display: inline-block;
  width: 370px !important;
  text-align: left;
  margin-left: 10px;
}

.hs6_code {
  display: inline-block;
  width: 70px !important;
  text-align: left;
}

.number-col {
  display: inline-block;
  width: 20px !important;
  text-align: left;
}

.no-link {
  text-decoration: none;
}

.section {
  min-width: 300px;
}

.opportunities {
  text-align: center;
}

.tariff {
  min-width: 50px;
}

.reo {
  text-align: center;
}
</style>
